import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BookText, ListVideo, Settings, AlertTriangle } from 'lucide-react';
import { useGetCourseForEditingQuery } from '../../api/apiSlice';
import CourseDetailsForm from '../../components/instructor/course-editor/CourseDetailsForm';
import CurriculumBuilder from '../../components/instructor/course-editor/CurriculumBuilder';
import CourseSettingsForm from '../../components/instructor/course-editor/CourseSettingsForm';
import ErrorBoundary from '../common/ErrorBoundary';
import Loading from '../../components/common/Loading';

// Статусы, на которых редактирование структуры (curriculum, обложка, title)
// заблокировано — Details/Settings показываем, но в read-only режиме для
// небезопасных полей. Curriculum-вкладку прячем целиком.
const FULL_EDIT_LOCKED_STATUSES = new Set([
  'active',
  'pending_review',
  'archived',
]);

const TABS = {
  DETAILS: 'details',
  CURRICULUM: 'curriculum',
  SETTINGS: 'settings',
};

const STATUS_LABEL_KEYS = {
  draft: 'course.status.draft',
  pending_review: 'course.status.pending_review',
  active: 'course.status.active',
  rejected: 'course.status.rejected',
  inactive: 'course.status.rejected',
  archived: 'course.status.archived',
};

const STATUS_BADGE_CLASSES = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  pending_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  inactive: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  archived: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
};

const CourseEditorPage = () => {
  const { courseId } = useParams();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TABS.DETAILS);

  const { data: course, isLoading, isFetching, isError, error } = useGetCourseForEditingQuery(courseId);

  const TabButton = ({ tabName, icon, label }) => (
    <button
      type="button"
      onClick={() => setActiveTab(tabName)}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
        activeTab === tabName
          ? 'bg-indigo-600 text-white shadow-sm'
          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  if (isLoading || isFetching) {
    return <Loading variant="card" label={t('common.loading')} description={t('editor.curriculumTitle')} />;
  }

  if (isError || !course) {
    return (
      <div className="max-w-md mx-auto rounded-2xl border border-red-100 dark:border-red-900/40 bg-white dark:bg-slate-900 p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-500/10">
          <AlertTriangle className="h-5 w-5 text-red-500" />
        </div>
        <p className="text-base font-semibold text-slate-900 dark:text-white">{t('errors.generic')}</p>
        {error?.data?.message && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{error.data.message}</p>
        )}
        <Link
          to="/dashboard/instructor/my-courses"
          className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          <ArrowLeft size={14} />
          {t('common.back')}
        </Link>
      </div>
    );
  }

  const statusKey = STATUS_LABEL_KEYS[course.status] || 'course.status.draft';
  const statusClass = STATUS_BADGE_CLASSES[course.status] || STATUS_BADGE_CLASSES.draft;
  // Ключевой свитч: для опубликованных / на модерации / в архиве курсов
  // НЕ показываем вкладки структуры. Только safe-panel с урезанным набором.
  const fullEditLocked = FULL_EDIT_LOCKED_STATUSES.has(course.status);

  return (
    <div>
      <Link
        to="/dashboard/instructor/my-courses"
        className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 mb-6"
      >
        <ArrowLeft size={16} />
        {t('common.back')}
      </Link>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm">
        <div className="p-5 sm:p-6 border-b dark:border-slate-800">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {course.title || t('editor.newSection')}
            </h1>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClass}`}>
              {t(statusKey)}
            </span>
          </div>

          {/* Табы. Curriculum скрываем на locked-курсе (там полно destructive
              кнопок — добавить/удалить секцию/урок/видео). Details и Settings
              остаются, но сами знают про locked и блокируют небезопасные поля. */}
          <div className="mt-4 flex flex-wrap gap-2">
            <TabButton tabName={TABS.DETAILS} icon={<BookText size={16} />} label={t('editor.tabs.details')} />
            {!fullEditLocked && (
              <TabButton tabName={TABS.CURRICULUM} icon={<ListVideo size={16} />} label={t('editor.tabs.curriculum')} />
            )}
            <TabButton tabName={TABS.SETTINGS} icon={<Settings size={16} />} label={t('editor.tabs.settings')} />
          </div>
        </div>

        <div className="p-5 sm:p-8">
          {/* Баннер «курс заморожен» на самом верху — единое объяснение
              почему какие-то поля недоступны. Замочки внутри форм просто
              дублируют статус, но баннер задаёт контекст сверху. */}
          {fullEditLocked && (
            <div className="mb-5 rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4 text-sm text-amber-900 dark:text-amber-100">
              <b>Курс {course.status === 'pending_review' ? 'на модерации' : course.status === 'archived' ? 'в архиве' : 'опубликован'}.</b>
              {' '}Изменить можно только цену, скидку, описание, категорию, язык и
              ссылки сообщества. Название, обложка, превью и уроки заморожены —
              иначе пострадают те, кто уже купил курс.
            </div>
          )}
          <ErrorBoundary>
            {activeTab === TABS.DETAILS && (
              <CourseDetailsForm course={course} isLocked={fullEditLocked} />
            )}
            {activeTab === TABS.CURRICULUM && !fullEditLocked && (
              <CurriculumBuilder
                courseId={course._id}
                initialSections={Array.isArray(course.sections) ? course.sections : []}
              />
            )}
            {activeTab === TABS.SETTINGS && (
              <CourseSettingsForm course={course} isLocked={fullEditLocked} />
            )}
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

export default CourseEditorPage;
